import { AssessmentOutlined } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { CoverageAssistantFilters } from '@procureai/ui-modules-mui';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { BaseDetailDomainType, I18nNamespace, QueryParam } from '~/common/types';
import { QuerySavePopover } from '~/components/DataGridQueryBuilder/components/QuerySavePopover';
import { Filter, Filters, FilterSaveStatus, useFilters } from '~/components/Filters';
import { usePopover } from '~/hooks';
import { usePersistedJson } from '~/hooks/usePersistedJson/usePersistedJson';

import { CoverageAssistantFiltersForm } from './components/CoverageAssistantFiltersForm/CoverageAssistantFiltersForm';
import { CoverageAssistantPredictions } from './components/CoverageAssistantPredictions/CoverageAssistantPredictions';
import { coverageAssistantFiltersSerde } from './persistence';

const domain = BaseDetailDomainType.COVERAGE_ASSISTANT;

export const CoverageAssistantWidget = () => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'CoverageAssistantWidget' });
  const { t: tCommon } = useTranslation(undefined, { keyPrefix: 'Base.common' });
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get(QueryParam.QUERY) ?? undefined;

  const [persistedFilters, setPersistedFilters, { error: persistError }] = usePersistedJson<CoverageAssistantFilters>(
    domain,
    q,
    coverageAssistantFiltersSerde,
  );
  const formReady = !q || persistedFilters !== undefined || Boolean(persistError);

  const handleFiltersChange = useCallback(
    (data: CoverageAssistantFilters) => {
      setPersistedFilters(data);
    },
    [setPersistedFilters],
  );

  const { onFilterSave, saveStatus, selected, ...filtersProps } = useFilters({ domain, queryId: q });
  const { triggerProps, popoverProps } = usePopover();

  const handleFilterSelect = useCallback(
    (filter: Filter) => {
      const params = new URLSearchParams(searchParams);
      params.set(QueryParam.QUERY, filter.queryId);
      params.delete(QueryParam.ENCODED_QUERY);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return (
    <Widget data-intercom-target="CoverageAssistantWidget">
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<AssessmentOutlined fontSize="small" />} />}
        actionsRight={
          <Stack direction="row" spacing={2} alignItems="center">
            <Filters {...filtersProps} selected={selected} onFilterSelect={handleFilterSelect} />
            <Button
              variant="text"
              size="small"
              disabled={saveStatus === FilterSaveStatus.DISABLED}
              onClick={triggerProps.onClick}
            >
              <Typography variant="button">{tCommon('filterSave')}</Typography>
            </Button>
          </Stack>
        }
      />
      <WidgetContent>
        <Stack spacing={4} gap={4}>
          {formReady && (
            <CoverageAssistantFiltersForm
              key={q ?? 'default'}
              initialValues={persistedFilters}
              onChange={handleFiltersChange}
            />
          )}
          <CoverageAssistantPredictions filters={persistedFilters ?? null} />
        </Stack>
      </WidgetContent>
      <QuerySavePopover {...popoverProps} inputValue={selected?.name ?? ''} onSave={onFilterSave} />
    </Widget>
  );
};
